// src/components/DecisionMemo/BEPMemoDisplay.js
// Renders Business Evolution Pyramid (BEP) Decision Memos for Citrin Cooperman
// Parses markdown with BEP structure and displays with professional formatting

import React, { useMemo } from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './BEPMemoDisplay.css';

/**
 * BEP Section IDs and their display properties
 * 
 * ORDER UPDATED 2025-11-29: Matches ccFramework.js BEP_FRAMEWORK.sections
 * New order: Tone → Risk → Framework → Ops → Value
 * Rationale: Present risks before upside for board credibility
 * 
 * TITLES UPDATED to match backend prompt generation:
 * - "Corporate Sustainability & Risk Assessment" → "Risk & Governance Assessment"
 * - "Maximizing Operational Efficiency" → "Operations Readiness"
 */
const BEP_SECTIONS = [
  { id: 'tone_at_top', level: 1, title: 'Tone at the Top Assessment', searchPattern: /Level\s*1[:\s]+Tone/i, color: '#00A3E0' },
  { id: 'sustainability_risk', level: 2, title: 'Risk & Governance Assessment', searchPattern: /Level\s*2[:\s]+Risk/i, color: '#FFC107' },
  { id: 'business_framework', level: 3, title: 'Business Framework Requirements', searchPattern: /Level\s*3[:\s]+Business/i, color: '#4A4A4A' },
  { id: 'operational_efficiency', level: 4, title: 'Operations Readiness', searchPattern: /Level\s*4[:\s]+Operations?/i, color: '#28A745' },
  { id: 'shareholder_value', level: 5, title: 'Shareholder Value Impact', searchPattern: /Level\s*5[:\s]+Shareholder/i, color: '#005DAA' }
];

// ========================================================================
// FIXED parseBEPMarkdown function (Now extracts BLUF content and cleans it)
// ========================================================================

/**
 * Cleans BLUF content by removing structural markers that shouldn't display
 * @param {string} bluf - Raw BLUF content
 * @returns {string} Cleaned BLUF content
 */
function cleanBlufContent(bluf) {
  if (!bluf) return '';
  
  let cleaned = bluf;
  
  // Remove "PART 1:" header variations (with or without markdown formatting)
  // Matches: "PART 1:", "## PART 1:", "### PART 1:", "PART 1: BUSINESS EVOLUTION...", etc.
  cleaned = cleaned.replace(/^(#{1,3}\s*)?\s*PART\s*1[:\s].*$/gim, '');
  
  // Remove "BUSINESS EVOLUTION PYRAMID EXECUTIVE SUMMARY" if it appears standalone
  cleaned = cleaned.replace(/^(#{1,3}\s*)?\s*BUSINESS\s+EVOLUTION\s+PYRAMID\s+(EXECUTIVE\s+)?SUMMARY.*$/gim, '');
  
  // Remove any resulting excessive blank lines (more than 2 consecutive)
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  
  // Trim leading/trailing whitespace
  cleaned = cleaned.trim();
  
  return cleaned;
}

/**
 * Parses BEP markdown into sections
 * FIXED VERSION: Now extracts BLUF/Executive Snapshot before Level 1 and cleans it
 * @param {string} markdown - Full Decision Memo markdown content
 * @returns {object} { blufContent: string, bepSummary: string, detailedAnalysis: string, sections: array }
 */
function parseBEPMarkdown(markdown) {
  if (!markdown) {
    return { blufContent: '', bepSummary: '', detailedAnalysis: '', sections: [] };
  }

  let bepSummaryText = '';
  let detailedAnalysisText = '';
  
  const part2Match = markdown.match(/(?:^|\n)(#{1,3}\s*)?PART\s*2[:\s]/im);
  const detailedMatch = markdown.match(/(?:^|\n)(#{1,3}\s*)?DETAILED\s+\d+-FIELD/im);
  const splitIndex = part2Match?.index ?? detailedMatch?.index ?? -1;
  
  if (splitIndex !== -1) {
    bepSummaryText = markdown.substring(0, splitIndex);
    detailedAnalysisText = markdown.substring(splitIndex);
  } else {
    const detailedIndex = markdown.indexOf('DETAILED');
    if (detailedIndex !== -1) {
      bepSummaryText = markdown.substring(0, detailedIndex);
      detailedAnalysisText = markdown.substring(detailedIndex);
    } else {
      bepSummaryText = markdown;
    }
  }

  const sectionPositions = [];
  BEP_SECTIONS.forEach((section) => {
    const match = bepSummaryText.match(section.searchPattern);
    if (match) {
      const lineStart = bepSummaryText.lastIndexOf('\n', match.index) + 1;
      sectionPositions.push({ section, position: lineStart });
    }
  });
  sectionPositions.sort((a, b) => a.position - b.position);

  // Extract BLUF content (everything before Level 1) and clean it
  let blufContent = '';
  if (sectionPositions.length > 0) {
    const rawBluf = bepSummaryText.substring(0, sectionPositions[0].position).trim();
    blufContent = cleanBlufContent(rawBluf);
  }

  const sections = BEP_SECTIONS.map((section) => {
    const posInfo = sectionPositions.find(p => p.section.id === section.id);
    if (!posInfo) {
      return { ...section, content: 'No content available for this section.' };
    }
    const currentPosIdx = sectionPositions.findIndex(p => p.section.id === section.id);
    const nextPosInfo = sectionPositions[currentPosIdx + 1];
    const endPosition = nextPosInfo ? nextPosInfo.position : bepSummaryText.length;
    const rawSection = bepSummaryText.substring(posInfo.position, endPosition);
    const lines = rawSection.split('\n');
    const content = lines.slice(1).join('\n').trim();
    return { ...section, content: content || 'No content available for this section.' };
  });

  return { blufContent, bepSummary: bepSummaryText, detailedAnalysis: detailedAnalysisText, sections };
}

/**
 * BEPMemoDisplay Component
 * 
 * @param {object} followUpData - Decision Memo follow-up object
 * @param {string} followUpData.response - Full markdown content
 * @param {string} followUpData.framework - Should be 'business_evolution_pyramid'
 * @param {string} searchQuery - Optional search query for highlighting
 */
function BEPMemoDisplay({ followUpData, searchQuery = '' }) {
  
  // Helper function to highlight search terms
  const highlightSearchTerms = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(re);
    return parts.map((part, i) =>
      i % 2 === 1
        ? <span key={i} style={{ backgroundColor: '#ffeb3b', fontWeight: 'bold' }}>{part}</span>
        : <span key={i}>{part}</span>
    );
  };

  // Helper to process React children and apply highlighting
  const processTextChildren = (children, searchTerm) => {
    if (!searchTerm) return children;
    return React.Children.map(children, child => {
      if (typeof child === 'string') {
        return highlightSearchTerms(child, searchTerm);
      }
      return child;
    });
  };

  // Custom ReactMarkdown components for search highlighting
  const getHighlightedMarkdownComponents = (searchTerm) => {
    if (!searchTerm?.trim()) return {};
    return {
      p: ({children, ...props}) => <p {...props}>{processTextChildren(children, searchTerm)}</p>,
      li: ({children, ...props}) => <li {...props}>{processTextChildren(children, searchTerm)}</li>,
      td: ({children, ...props}) => <td {...props}>{processTextChildren(children, searchTerm)}</td>,
      th: ({children, ...props}) => <th {...props}>{processTextChildren(children, searchTerm)}</th>,
      strong: ({children, ...props}) => <strong {...props}>{processTextChildren(children, searchTerm)}</strong>,
      em: ({children, ...props}) => <em {...props}>{processTextChildren(children, searchTerm)}</em>,
    };
  };
  // Parse markdown into sections (now includes cleaned blufContent)
  const { blufContent, sections, detailedAnalysis } = useMemo(() => {
    return parseBEPMarkdown(followUpData.answer || followUpData.response);
  }, [followUpData.answer, followUpData.response]);

  return (
    <Box className="bep-memo-container">
      {/* BLUF / Executive Snapshot - Rendered before BEP sections */}
      {blufContent && (
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mb: 3,
            backgroundColor: '#f8f9fa',
            borderLeft: '4px solid #005DAA'
          }}
        >
          <Box 
            sx={{
              '& h1, & h2, & h3': { 
                mt: 2, 
                mb: 1.5, 
                fontWeight: 600,
                color: '#1a1a1a'
              },
              '& p': { mb: 1.5, lineHeight: 1.7 },
              '& strong': { fontWeight: 600 },
              '& ul, & ol': { pl: 3, mb: 2 },
              '& li': { mb: 0.5 },
              '& hr': { my: 2, border: 'none', borderTop: '1px solid #e0e0e0' }
            }}
          >
            <ReactMarkdown 
  remarkPlugins={[remarkGfm]}
  components={getHighlightedMarkdownComponents(searchQuery)}
>
  {blufContent}
</ReactMarkdown>
          </Box>
        </Paper>
      )}

      {/* BEP Executive Summary Header */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #005DAA 0%, #00A3E0 100%)',
          color: 'white'
        }}
      >
        <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
          Business Evolution Pyramid
        </Typography>
        <Typography variant="subtitle1" sx={{ opacity: 0.95 }}>
          Strategic Executive Summary
        </Typography>
      </Paper>

      {/* BEP Sections */}
      <Box className="bep-sections" sx={{ mb: 4 }}>
        {sections.map((section, index) => (
          <Paper 
            key={section.id} 
            elevation={1}
            className="bep-section"
            sx={{ 
              p: 3, 
              mb: 2,
              borderLeft: `4px solid ${section.color}`,
              '&:hover': {
                elevation: 3,
                transform: 'translateX(4px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            {/* Section Header */}
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  backgroundColor: section.color,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  mr: 2,
                  flexShrink: 0
                }}
              >
                {section.level}
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  component="h3" 
                  fontWeight={600}
                  sx={{ color: section.color }}
                >
                  {section.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Level {section.level}
                </Typography>
              </Box>
            </Box>

            {/* Section Content */}
<Box className="bep-section-content">
  <ReactMarkdown 
    remarkPlugins={[remarkGfm]}
    components={getHighlightedMarkdownComponents(searchQuery)}
  >
    {section.content}
  </ReactMarkdown>
</Box>
          </Paper>
        ))}
      </Box>

      {/* Divider before detailed analysis */}
      {detailedAnalysis && (
        <>
          <Divider sx={{ my: 4 }}>
            <Typography variant="overline" color="text.secondary" fontWeight={600}>
              Detailed Strategic Analysis
            </Typography>
          </Divider>

          {/* Detailed 11-Field Analysis */}
          <Paper 
            elevation={0} 
            variant="outlined"
            sx={{ 
              p: 3,
              backgroundColor: 'grey.50'
            }}
          >
            <Box 
              className="detailed-analysis-content"
              sx={{
                '& h1, & h2, & h3': { 
                  color: '#005DAA',
                  mt: 3,
                  mb: 2
                },
                '& p': { mb: 2, lineHeight: 1.7 },
                '& ul, & ol': { 
                  pl: 3,
                  mb: 2
                },
                '& li': { mb: 0.5 },
                '& strong': { fontWeight: 600, color: '#333' },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 2
                },
                '& th, & td': {
                  border: '1px solid #ddd',
                  padding: '8px',
                  textAlign: 'left'
                },
                '& th': {
                  backgroundColor: '#f2f2f2',
                  fontWeight: 600
                }
              }}
            >
              <ReactMarkdown 
  remarkPlugins={[remarkGfm]}
  components={getHighlightedMarkdownComponents(searchQuery)}
>
  {detailedAnalysis}
</ReactMarkdown>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}

export default BEPMemoDisplay;